// ─────────────────────────────────────────────────────────────────────────────
// COTIZAR UNA DIVISA, CON REINTENTOS (fase70)
//
// POR QUÉ EXISTE: el 4/11 la consola mostró PRE-04 («EUR, CHF sin cotización»)
// y al recargar volvió a verde. La cotización era UN pedido a Frankfurter, sin
// tiempo máximo: una sola falla dejaba la divisa sin cotización y encendía la
// alarma, y un pedido colgado colgaba el libro entero, que lo espera.
//
// Ahora cada cotización tiene hasta INTENTOS intentos sobre la MISMA fuente y
// con el MISMO pedido, cada uno con un tiempo máximo, y una espera corta entre
// uno y otro. Nada de valores de respaldo ni de precios viejos: si fallan
// todos, `null`, y la divisa queda sin cotización como antes (PRE-04, o PRE-05
// si la bloqueó la lista blanca).
//
// LA URL NO VIVE ACÁ. La arma `libro.ts`, que es donde `check-frankfurter.mjs`
// fija el literal (host, endpoint y parámetros, iguales a los del composer).
// Este módulo sólo sabe intentar, esperar y leer `rates.USD`. No importa nada:
// así se prueba solo (`cotizacion.test.ts`), sin Firebase ni React.
// ─────────────────────────────────────────────────────────────────────────────

/** Intentos por cotización. */
export const INTENTOS = 3;
/** Tiempo máximo de cada intento, en ms. */
export const TIEMPO_MAX_MS = 5_000;
/** Espera entre un intento y el siguiente, en ms. */
export const ESPERA_MS = 1_000;

/** Lo único que se usa de la respuesta de `fetch`. */
export type Respuesta = { ok: boolean; json: () => Promise<unknown> };

/** Hace UN pedido a la fuente. Recibe la señal que lo corta al vencer su tiempo. */
export type Pedido = (signal: AbortSignal) => Promise<Respuesta>;

export type Opciones = { intentos?: number; tiempoMaxMs?: number; esperaMs?: number };

const esperar = (ms: number) => new Promise<void>((listo) => setTimeout(listo, ms));

/** Un intento: la respuesta tiene que ser `ok` y traer un USD positivo. */
async function intentar(pedir: Pedido, tiempoMaxMs: number): Promise<number | null> {
  const corte = new AbortController();
  let reloj: ReturnType<typeof setTimeout> | undefined;
  // La señal corta el pedido de verdad. La carrera contra el mismo reloj
  // garantiza el tope aunque el pedido ignore la señal, y cubre también la
  // lectura del cuerpo.
  const vencido = new Promise<never>((_, rechazar) => {
    reloj = setTimeout(() => {
      corte.abort();
      rechazar(new Error(`sin respuesta en ${tiempoMaxMs} ms`));
    }, tiempoMaxMs);
  });
  try {
    const r = await Promise.race([pedir(corte.signal), vencido]);
    if (!r.ok) return null;
    const j = (await Promise.race([r.json(), vencido])) as { rates?: { USD?: unknown } } | null;
    const usd = Number(j?.rates?.USD);
    return usd > 0 ? usd : null;
  } catch {
    // Red caída, bloqueo de la lista blanca o tiempo agotado. Desde acá son
    // indistinguibles: PRE-05 los separa aparte, con el evento del navegador.
    return null;
  } finally {
    if (reloj !== undefined) clearTimeout(reloj);
  }
}

/**
 * {base}/USD con hasta `intentos` intentos. Devuelve el número, o `null` si
 * fallaron todos: la divisa queda sin cotización, igual que antes de fase70.
 */
export async function cotizar(pedir: Pedido, opciones: Opciones = {}): Promise<number | null> {
  const { intentos = INTENTOS, tiempoMaxMs = TIEMPO_MAX_MS, esperaMs = ESPERA_MS } = opciones;
  for (let intento = 1; intento <= intentos; intento++) {
    const usd = await intentar(pedir, tiempoMaxMs);
    if (usd !== null) return usd;
    if (intento < intentos) await esperar(esperaMs);
  }
  return null;
}
